import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

const authHeaders = () => ({ Authorization: getAccessTokenApi() });

/**
 * Wrapper de los endpoints /liftlogic/menu-items.
 *
 * No maneja estado (no usa useState): cada método retorna la Promise del
 * axios. El componente que lo consume es el responsable de guardar datos,
 * loading y errores. Así el hook se puede reusar en formularios, modales
 * y toolbars sin conflictos.
 */
const useMenuItemsApi = () => {
  const list = useCallback(async (tenantId) => {
    const { data } = await axios.get(`${API_PATH}/liftlogic/menu-items?tenantId=${tenantId}`, {
      headers: authHeaders(),
    });
    return data;
  }, []);

  const create = useCallback(async (payload) => {
    const { data } = await axios.post(`${API_PATH}/liftlogic/menu-items`, payload, {
      headers: authHeaders(),
    });
    return data;
  }, []);

  const update = useCallback(async (id, tenantId, payload) => {
    const { data } = await axios.put(
      `${API_PATH}/liftlogic/menu-items/${id}`,
      { ...payload, tenantId },
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const remove = useCallback(async (id, tenantId) => {
    const { data } = await axios.delete(
      `${API_PATH}/liftlogic/menu-items/${id}?tenantId=${tenantId}`,
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const reorder = useCallback(async (tenantId, updates) => {
    const { data } = await axios.patch(
      `${API_PATH}/liftlogic/menu-items/reorder`,
      { tenantId, updates },
      { headers: authHeaders() }
    );
    return data;
  }, []);

  const exportXlsx = useCallback(async (tenantId) => {
    const response = await axios.get(
      `${API_PATH}/liftlogic/menu-items/export?tenantId=${tenantId}`,
      { headers: authHeaders(), responseType: 'blob' }
    );
    // Dispara el download en el navegador.
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `menu-items-tenant-${tenantId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const importXlsx = useCallback(async (tenantId, file, mode) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(
      `${API_PATH}/liftlogic/menu-items/import?tenantId=${tenantId}&mode=${mode}`,
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  }, []);

  return useMemo(
    () => ({
      list,
      create,
      update,
      remove,
      reorder,
      exportXlsx,
      importXlsx,
    }),
    [list, create, update, remove, reorder, exportXlsx, importXlsx]
  );
};

export default useMenuItemsApi;
